import {
  AlertCircle,
  CheckCircle2,
  CloudOff,
  ExternalLink,
  Loader2,
  Phone,
  Unplug,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDisconnectRCOAuthMutation,
  useGetRCOAuthStatusQuery,
} from '@/services/api/ringcentralService';

const BACKEND_HOST =
  import.meta.env.VITE_APP_DJANGO_BACKEND_HOST || 'http://localhost:8000';

const RingCentralOAuthSkeleton: React.FC = () => (
  <div className="border rounded-lg bg-white overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <Skeleton className="h-6 w-24 rounded-full" />
    </div>
  </div>
);

const RingCentralLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect rx="8" width="40" height="40" fill="#FF8800" />
    <circle cx="20" cy="20" r="10" fill="white" />
    <circle cx="20" cy="20" r="5" fill="#FF8800" />
  </svg>
);

export const RingCentralOAuthIntegration: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const {
    data: status,
    isLoading,
    refetch,
  } = useGetRCOAuthStatusQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [disconnectRC, { isLoading: isDisconnecting }] =
    useDisconnectRCOAuthMutation();

  // Show success/error toast when redirected back from OAuth
  useEffect(() => {
    const rcParam = searchParams.get('rc');
    if (rcParam === 'connected') {
      toast.success('RingCentral connected successfully');
      searchParams.delete('rc');
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
      refetch();
    } else if (rcParam === 'error') {
      toast.error(
        'Failed to connect RingCentral. Please try again or contact support.',
      );
      searchParams.delete('rc');
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch]);

  const handleConnect = () => {
    // The backend connect endpoint uses the session cookie to identify the user.
    // origin tells the callback the frontend base; redirect_to brings user back to this page.
    const origin = encodeURIComponent(window.location.origin);
    const redirectTo = encodeURIComponent(window.location.href);
    window.location.href = `${BACKEND_HOST}/api/integrations/ringcentral/oauth/connect?origin=${origin}&redirect_to=${redirectTo}`;
  };

  const handleDisconnect = async () => {
    try {
      await disconnectRC().unwrap();
      toast.success('RingCentral disconnected');
      setIsDisconnectDialogOpen(false);
    } catch {
      toast.error('Failed to disconnect RingCentral');
    }
  };

  if (isLoading) {
    return <RingCentralOAuthSkeleton />;
  }

  const isConnected = status?.connected;

  return (
    <>
      <div className="border rounded-lg bg-white overflow-hidden">
        {/* Card header: logo, name, description, status */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-50 border border-orange-100">
              <RingCentralLogo className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  RingCentral
                </h3>
                <span className="text-xs text-gray-500">
                  Business phone & messaging
                </span>
              </div>
              {isConnected ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  {status.extension_name || 'RingCentral User'}
                  {status.extension_number
                    ? ` \u00B7 Ext. ${status.extension_number}`
                    : ''}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-0.5">
                  Connect your RingCentral account for calls and SMS
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isConnected ? (
              <>
                <Badge
                  variant="default"
                  className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 font-medium"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDisconnectDialogOpen(true)}
                  className="text-gray-500 hover:text-red-600 h-8 px-2"
                >
                  <Unplug className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button onClick={handleConnect} size="sm" className="h-8">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Connect
              </Button>
            )}
          </div>
        </div>

        {/* Connected: active status banner */}
        {isConnected && (
          <div className="border-t bg-orange-50/50 px-5 py-3">
            <div className="flex items-center gap-2 text-xs text-orange-700">
              <Phone className="h-3.5 w-3.5 shrink-0 text-orange-600" />
              <span className="font-medium">
                Calls and SMS will use your personal RingCentral account.
              </span>
            </div>
          </div>
        )}

        {/* Disconnected: empty state */}
        {!isConnected && (
          <div className="border-t bg-gray-50/50 px-5 py-3">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <CloudOff className="h-3.5 w-3.5 shrink-0" />
              <span>
                No account connected. Click Connect to authorize via RingCentral
                OAuth.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Disconnect confirmation dialog */}
      <Dialog
        open={isDisconnectDialogOpen}
        onOpenChange={setIsDisconnectDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect RingCentral</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect your RingCentral account?
              Calls and SMS will no longer use your personal account.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertCircle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
            <p>
              You can reconnect at any time. Existing call and message history
              will remain in Finch.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDisconnectDialogOpen(false)}
              disabled={isDisconnecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
